import React, { useState, useEffect } from 'react';
import { Calendar, User, Phone, Truck, Settings, Users, ChevronDown, ChevronUp } from 'lucide-react';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import FormInput from '../../../../../Component/ui/FormInput.jsx';
import axios from '../../../../../axiosInstance.js';
import { useModal } from '../../../../../context/ModalContext.jsx';

const DriverDetailsStep = ({ onNext, onPrevious, formData, setFormData }) => {
  const { openModal, modalData, closeModal } = useModal();
  const [errors, setErrors] = useState({});
  const [siteManagerName, setSiteManagerName] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [loadingVehicleDetails, setLoadingVehicleDetails] = useState(false);

  // Set current date and site manager on load
  useEffect(() => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    const vendorName = localStorage.getItem('vendorName') || 'Site Manager';
    
    setSiteManagerName(vendorName);
    setFormData(prev => ({
      ...prev,
      driverDetails: {
        ...prev.driverDetails,
        inspectionDate: dateStr,
        siteManager: vendorName
      }
    }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      driverDetails: {
        ...prev.driverDetails,
        [name]: value
      }
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.driverDetails?.driverName) newErrors.driverName = 'Driver name is required';
    if (!formData.driverDetails?.mobileNo) newErrors.mobileNo = 'Mobile number is required';
    else if (!/^\d{10}$/.test(formData.driverDetails.mobileNo.replace(/[^\d]/g, ''))) {
      newErrors.mobileNo = 'Mobile number must be 10 digits';
    }
    if (!formData.driverDetails?.vehicleNo) newErrors.vehicleNo = 'Vehicle number is required';
    if (!formData.driverDetails?.model) newErrors.model = 'Vehicle model is required';
    if (!formData.driverDetails?.vehicleType) newErrors.vehicleType = 'Vehicle type is required';
    if (!formData.driverDetails?.clientManager) newErrors.clientManager = 'Client manager is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const handleReset = () => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    setFormData(prev => ({
      ...prev,
      driverDetails: {
        driverName: '',
        mobileNo: '',
        vehicleNo: '',
        model: '',
        vehicleType: '',
        inspectionDate: dateStr,
        siteManager: siteManagerName,
        clientManager: ''
      }
    }));
    setErrors({});
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/[^\d]/g, '');
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 10) {
      return cleaned;
    }
    return cleaned.slice(0, 10);
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    handleChange({ target: { name: 'mobileNo', value: formatted } });
  };

  // Fetch vehicle details from API
  const fetchVehicleDetails = async (vehicleNumber) => {
    if (!vehicleNumber || vehicleNumber.length < 8) return;

    try {
      setLoadingVehicleDetails(true);
      const response = await axios.post(
        "/api/fetchVehicleDetails",
        JSON.stringify({ vehiclenumber: vehicleNumber }),
        {
          baseURL: process.env.REACT_APP_BACKEND_URL,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status !== 200) {
        openModal({
          title: 'Error',
          body: 'Server error. Please try again later.',
          iconType: 'error',
          autoClose: true,
        });
        return;
      }

      const data = response.data;

      if (!data || data.success !== true) {
        openModal({
          title: 'Error',
          body: 'Unable to fetch vehicle details...',
          iconType: 'error',
          autoClose: true,
        });
        return;
      }

      if (!data.data || Object.keys(data.data).length === 0) {
        openModal({
          title: 'Error',
          body: 'No vehicle details found for the given number.',
          iconType: 'error',
          autoClose: true,
        });
        return;
      }

      if (response.data?.success && response.data?.data?.VehicleDetails) {
        const vehicleData = response.data.data.VehicleDetails;
        
        // Auto-populate vehicle fields
        setFormData(prev => ({
          ...prev,
          driverDetails: {
            ...prev.driverDetails,
            model: vehicleData.rc_maker_model || prev.driverDetails.model,
            vehicleType: vehicleData.rc_body_type_desc || prev.driverDetails.vehicleType
          },
          vehicleUlipData: {
            rc_vh_class_desc:  vehicleData.rc_vh_class_desc  || '',
            rc_body_type_desc: vehicleData.rc_body_type_desc || '',
            rc_permit_type:    vehicleData.rc_permit_type    || '',
            rc_no_of_axle:     vehicleData.rc_no_of_axle     || '',
            rc_maker_model:    vehicleData.rc_maker_model    || '',

            rc_chasi_no:          vehicleData.rc_chasi_no          || '',
            rc_regn_dt:           vehicleData.rc_regn_dt           || '',
            rc_insurance_upto:    vehicleData.rc_insurance_upto    || '',
            rc_permit_valid_upto: vehicleData.rc_permit_valid_upto || '',
            rc_np_upto:           vehicleData.rc_np_upto           || '',
            rc_fit_upto:          vehicleData.rc_fit_upto          || '',
            rc_tax_upto:          vehicleData.rc_tax_upto          || '',
            rc_pucc_upto:         vehicleData.rc_pucc_upto         || '',
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching vehicle details:', error);
      openModal({
        title: 'Error',
        body: 'Something went wrong while fetching vehicle data.',
        iconType: 'error',
        autoClose: true,
      });
    } finally {
      setLoadingVehicleDetails(false);
    }
  };

  // Handle vehicle number change with auto-fetch
  const handleVehicleNumberChange = (e) => {
    const value = e.target.value.toUpperCase();
    handleChange({ target: { name: 'vehicleNo', value } });

    // Auto-fetch when vehicle number looks complete (10+ characters)
    if (value.replace(/[^A-Z0-9]/g, '').length >= 10) {
      fetchVehicleDetails(value);
    }
  };

  return (
    <div className="space-y-3 pb-16">
      {/* Heading */}
      <div className="flex items-center gap-2 pt-2 px-2">
        <AppRegistrationIcon className="text-[#21A6E9]" />
        <h4 className="text-[20px] md:text-[28px] font-[500] font-satoshi text-[#000000]">
          Driver Details
        </h4>
      </div>

      {/* Form Fields */}
      <FormInput
        label="Inspection Date"
        type="date"
        name="inspectionDate"
        value={formData.driverDetails?.inspectionDate || ''}
        icon={Calendar}
        disabled={true}
        borderStyle="gray"
        helperText="Today's date (auto-filled)"
        className="mb-3"
      />

      <FormInput
        label="Driver Name"
        type="text"
        name="driverName"
        value={formData.driverDetails?.driverName || ''}
        onChange={handleChange}
        placeholder="Enter driver name"
        icon={User}
        required={true}
        borderStyle="gray"
        error={errors.driverName}
        className="mb-3"
      />

      <FormInput
        label="Mobile Number"
        type="tel"
        name="mobileNo"
        value={formData.driverDetails?.mobileNo || ''}
        onChange={handlePhoneChange}
        placeholder="Enter 10-digit mobile number"
        icon={Phone}
        required={true}
        borderStyle="gray"
        maxLength="10"
        error={errors.mobileNo}
        className="mb-3"
      />

      <FormInput
        label="Vehicle Number"
        type="text"
        name="vehicleNo"
        value={formData.driverDetails?.vehicleNo || ''}
        onChange={handleVehicleNumberChange}
        placeholder="e.g., HR01AB1234"
        icon={Truck}
        required={true}
        borderStyle="gray"
        inputClassName="uppercase"
        error={errors.vehicleNo}
        className="mb-3"
        helperText={loadingVehicleDetails ? "Fetching vehicle details..." : ""}
      />

      <FormInput
        label="Model"
        type="text"
        name="model"
        value={formData.driverDetails?.model || ''}
        onChange={handleChange}
        placeholder="e.g., Tata 407"
        icon={Truck}
        required={true}
        borderStyle="gray"
        error={errors.model}
        className="mb-3"
      />

      <FormInput
        label="Vehicle Type"
        type="select"
        name="vehicleType"
        value={formData.driverDetails?.vehicleType || ''}
        onChange={handleChange}
        onFocus={() => setOpenDropdown('vehicleType')}
        onBlur={() => setOpenDropdown(null)}
        icon={Settings}
        required={true}
        borderStyle="gray"
        options={[
          'Crane',
          'Recovery Van',
          'Ambulance',
          'Towing'
        ]}
        openDropdown={openDropdown === 'vehicleType'}
        error={errors.vehicleType}
        className="mb-3"
      />

      <FormInput
        label="Site Manager"
        type="text"
        name="siteManager"
        value={formData.driverDetails?.siteManager || ''}
        onChange={handleChange}
        icon={User}
        borderStyle="gray"
        className="mb-3"
      />

      <FormInput
        label="Client Manager"
        type="text"
        name="clientManager"
        value={formData.driverDetails?.clientManager || ''}
        onChange={handleChange}
        placeholder="Enter client manager name"
        icon={Users}
        required={true}
        borderStyle="gray"
        error={errors.clientManager}
        className="mb-3"
      />
      <FormInput
        label="Driver Problem(s)"
        type="text"
        name="driverProblem"
        value={formData.driverProblem || ''}
        onChange={handleChange}
        placeholder="Enter driver problem"
        icon={Users}
        required={false}
        borderStyle="gray"
        error={errors.driverProblem}
        className="mb-3"
      />

      {/* Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-3 md:relative md:border-t-0 md:mt-8 md:gap-4">
        <button
          onClick={onPrevious}
          className="flex-1 px-6 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-100 transition font-semibold text-sm md:text-base"
        >
          Previous
        </button>
        <button
          onClick={handleReset}
          className="flex-1 px-6 py-3 border-2 border-gray-400 text-gray-700 rounded-lg hover:bg-gray-100 transition font-semibold text-sm md:text-base"
        >
          Reset
        </button>
        <button
          onClick={handleNext}
          className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold text-sm md:text-base shadow-md"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default DriverDetailsStep;
